import Button from '@jetbrains/ring-ui/components/button/button'
import {memo, Suspense, useCallback, useMemo, useRef, useState} from 'react'

import {usePermission} from '../../../../hooks/usePermission'
import {restApi} from '../../../../services/rest'
import type {BuildId, ProjectId} from '../../../../types'
import {Permission, ROOT_PROJECT_ID, toTestOccurrenceId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import type {Handle} from '../../../common/Tree/Tree'
import Tree from '../../../common/Tree/Tree'
import {getTreeNodes} from '../../../common/Tree/Tree.utils'
import {
  getProblemOccurrencesTreeNodes,
  getProblemsLeavesHash,
  getProblemsNodesHash,
} from '../BuildProblems.selectors'

import {useBuildProblemsTreeArg} from './BuildProblemsTree.hooks'
import {generateProblemsTree} from './BuildProblemsTree.utils'
import InvestigationMuteButtons from './InvestigationMuteButtons/InvestigationMuteButtons'

import styles from './BuildProblemsTree.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  labelId: string
  className?: string
  locator: string
  projectId?: ProjectId
  buildId?: BuildId
  hasRootCauses?: boolean
  rootCausesView?: boolean
}

function BuildProblemsTree({labelId, className, projectId, buildId, locator}: Props) {
  const treeRef = useRef<Handle>(null)
  const hasPermissionForInvestigation = usePermission(Permission.ASSIGN_INVESTIGATION, projectId)
  const hasPermissionForMute = usePermission(Permission.MANAGE_BUILD_PROBLEMS, projectId)
  const arg = useBuildProblemsTreeArg(locator)
  const {refetch} = restApi.endpoints.getProblemOccurrenceTree.useQuerySubscription(arg)
  const treeNodes = useAppSelector(state => getProblemOccurrencesTreeNodes(state, arg))
  const leavesHash = useAppSelector(state => getProblemsLeavesHash(state, arg))
  const nodesHash = useAppSelector(state => getProblemsNodesHash(state, arg))
  const treeRoot = useMemo(
    () =>
      treeNodes != null ? generateProblemsTree(treeNodes, leavesHash, locator, buildId) : null,
    [leavesHash, treeNodes, locator, buildId],
  )
  const treeRootChildren = treeRoot?.children ?? emptyArray
  const flatTreeNodes = useMemo(() => getTreeNodes(treeRootChildren), [treeRootChildren])
  const [checkedState, setCheckedState] = useState<string[]>([])

  const defaultExpandedState: readonly string[] = useMemo(() => {
    if (nodesHash[ROOT_PROJECT_ID]?.newFailedCount) {
      return flatTreeNodes
        .filter(node => nodesHash[node.value]?.newFailedCount)
        .map(node => `${node.value}`)
    }
    return flatTreeNodes.map(node => `${node.value}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [expandedState, setExpandedState] = useState(defaultExpandedState)
  const onCheck = useCallback(
    (newCheckedState: readonly string[]) =>
      setCheckedState(newCheckedState.map(toTestOccurrenceId)),
    [],
  )
  const onExpand = useCallback(
    (newExpandedState: readonly string[]) => setExpandedState(newExpandedState),
    [],
  )
  const expandedAll = useMemo(
    () => flatTreeNodes.every(node => expandedState.includes(`${node.value}`)),
    [expandedState, flatTreeNodes],
  )
  const toggleExpand = useCallback(() => {
    if (expandedAll && treeRef.current?.onCollapseAll != null) {
      treeRef.current.onCollapseAll()
    }

    if (!expandedAll && treeRef.current?.onExpandAll != null) {
      treeRef.current.onExpandAll()
    }
  }, [expandedAll])

  const onAssignInvestigationOrMute = useCallback(() => {
    setCheckedState([])
    refetch()
  }, [refetch])

  return (
    <div className={className}>
      <Button className={styles.expandAllButton} onClick={toggleExpand}>
        {expandedAll ? 'Collapse all' : 'Expand all'}
      </Button>
      {projectId != null && (
        <Suspense>
          <InvestigationMuteButtons
            className={styles.investigationMuteButtons}
            projectId={projectId}
            problemOccurrencesIds={checkedState}
            onAction={onAssignInvestigationOrMute}
          />
        </Suspense>
      )}
      <Tree
        labelId={labelId}
        id="overview-problems-tree"
        className={styles.tree}
        ref={treeRef}
        checkable={hasPermissionForInvestigation || hasPermissionForMute}
        checked={checkedState}
        expanded={expandedState}
        nodes={treeRootChildren}
        checkAllButtonName="All problems"
        onCheck={onCheck}
        onExpand={onExpand}
      />
      {treeRoot?.footerComponent}
    </div>
  )
}

export default memo<Props>(BuildProblemsTree)
